<template>
  <div class="products-page">
    <!-- 导航栏 -->
    <WebsiteNavbar />

    <div class="hero-section">
      <div class="hero-background">
        <img :src="heroBackgroundImage" alt="Products Background" />
        <div class="slide-overlay"></div>
      </div>
      <div class="container">
        <h1 class="page-title">{{ t('products.title') }}</h1>
        <p class="page-subtitle">{{ t('products.subtitle') }}</p>
      </div>
    </div>
    
    <div class="content-section">
      <div class="container">
        <div class="product-categories">
          <div class="category-tabs">
            <button
              v-for="category in categories"
              :key="category.id"
              :class="['category-tab', { active: activeCategory === category.id }]"
              @click="activeCategory = category.id"
              :disabled="loading"
            >
              {{ category.categoryName }}
            </button>
          </div>

          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>{{ t('common.loading') }}</p>
          </div>
          <div v-else class="products-grid">
            <div
              class="product-card"
              v-for="product in filteredProducts"
              :key="product.id"
            >
              <div class="product-image-wrapper">
                <img
                  :src="product.coverImage || placeholderImage"
                  :alt="product.productName"
                  class="product-image"
                  @error="$event.target.src = placeholderImage"
                />
              </div>
              <div class="product-info">
                <h3 class="product-name">{{ product.productName }}</h3>
                <p class="product-summary" v-if="product.productSummary">
                  {{ product.productSummary.length > 100 ? product.productSummary.substring(0, 100) + '...' : product.productSummary }}
                </p>
                <p class="category-name" v-if="product.categoryName">
                  {{ product.categoryName }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="!loading && filteredProducts.length === 0" class="no-data">
            <p>{{ t('common.noData') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <WebsiteFooter />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getCategories, getProducts } from '@/api/cms'
import WebsiteNavbar from './components/WebsiteNavbar.vue'
import WebsiteFooter from './components/WebsiteFooter.vue'

const { t, locale } = useI18n()
const heroBackgroundImage = new URL('@/assets/carousel/slide1.webp', import.meta.url).href
const placeholderImage = new URL('@/assets/placeholder.svg', import.meta.url).href

interface Category {
  id: number
  categoryName: string
  children?: Category[]
}

interface Product {
  id: number
  productName: string
  productSummary: string
  coverImage: string
  categoryId: number
  categoryName?: string
}

const loading = ref(false)
const activeCategory = ref(0) // 0表示全部

const categories = ref<Category[]>([])
const products = ref<Product[]>([])

const filteredProducts = computed(() => {
  if (activeCategory.value === 0) {
    return products.value
  }
  return products.value.filter(product => product.categoryId === activeCategory.value)
})

// 加载类别数据
const loadCategories = async () => {
  try {
    loading.value = true
    console.log('📂 Loading categories with locale:', locale.value)
    const response = await getCategories()
    console.log('📂 Categories response:', response)
    if (response.code === 200 && response.data) {
      // 添加"全部产品"选项
      const allCategory = { id: 0, categoryName: t('products.categories.all') }
      categories.value = [allCategory, ...response.data]
      console.log('✅ Categories loaded successfully:', categories.value.length, 'items')
    } else {
      console.warn('⚠️ Categories API returned unexpected response:', response)
    }
  } catch (error) {
    console.error('❌ 加载类别失败:', error)
    // 使用默认类别
    categories.value = [
      { id: 0, categoryName: t('products.categories.all') },
      { id: 1, categoryName: t('products.categories.actuators') },
      { id: 2, categoryName: t('products.categories.accessories') },
      { id: 3, categoryName: t('products.categories.controllers') },
      { id: 4, categoryName: t('products.categories.parts') }
    ]
  } finally {
    loading.value = false
  }
}

// 加载产品数据
const loadProducts = async (categoryId?: number) => {
  try {
    loading.value = true
    const params: any = {}
    if (categoryId && categoryId > 0) {
      params.categoryId = categoryId
    }
    const response = await getProducts(params)
    if (response.code === 200 && response.data) {
      products.value = response.data
    }
  } catch (error) {
    console.error('加载产品失败:', error)
    products.value = []
  } finally {
    loading.value = false
  }
}

// 监听语言变化，重新加载数据
watch(locale, (newLocale, oldLocale) => {
  console.log('🔄 Language changed in ProductsPage:', {
    from: oldLocale,
    to: newLocale,
    timestamp: new Date().toISOString()
  })
  console.log('🌐 Current localStorage locale:', localStorage.getItem('locale'))
  console.log('📡 Reload categories and products for new language')

  // 重新加载数据
  loadCategories()
  loadProducts(activeCategory.value)
})

// 监听类别变化，重新加载产品
watch(activeCategory, (newCategoryId) => {
  loadProducts(newCategoryId)
})

onMounted(() => {
  loadCategories()
  loadProducts()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.products-page {
  font-family: 'Hind', 'Bitter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

.hero-section {
  position: relative;
  color: white;
  padding: 120px 0 80px;
  text-align: center;
  margin-top: 75px;
}

.hero-background {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.hero-background img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4));
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
}

.page-title {
  font-size: 58px;
  font-weight: 700;
  margin-bottom: 28px;
  line-height: 1.2;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  font-family: 'Bitter', serif;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 20px;
  margin-bottom: 45px;
  opacity: 0.95;
  font-weight: 300;
  line-height: 1.7;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  font-family: 'Hind', sans-serif;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.content-section {
  padding: 90px 0;
  background-color: #f5f6f8;
}

.category-tabs {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 60px;
  flex-wrap: wrap;
}

.category-tab {
  padding: 12px 24px;
  border: none;
  background: white;
  color: #7f8c8d;
  font-size: 16px;
  font-weight: 500;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font-family: 'Hind', sans-serif;
}

.category-tab:hover {
  color: #d97f3e;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.category-tab.active {
  background: linear-gradient(135deg, #d97f3e 0%, #c06d2f 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(217, 127, 62, 0.4);
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  padding: 20px 0;
}

.product-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  border-color: #d97f3e;
}

.product-image-wrapper {
  width: 100%;
  height: 260px;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.product-card:hover .product-image {
  transform: scale(1.08);
}

.product-info {
  padding: 24px 20px;
  background: white;
}

.product-name {
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  line-height: 1.5;
  font-family: 'Bitter', serif;
  letter-spacing: 0.3px;
  margin: 0;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
}

.product-card:hover .product-name {
  color: #d97f3e;
}

.product-summary {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin: 8px 0;
  font-family: 'Hind', sans-serif;
}

.category-name {
  color: #d97f3e;
  font-size: 12px;
  margin: 8px 0 0 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #d97f3e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  color: #666;
  font-size: 16px;
  margin: 0;
}

.no-data {
  text-align: center;
  padding: 60px 0;
  color: #666;
  font-size: 16px;
}

@media (max-width: 1024px) {
  .products-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
  }

  .product-image-wrapper {
    height: 220px;
  }

  .product-name {
    font-size: 16px;
    min-height: 48px;
  }

  .product-info {
    padding: 20px 16px;
  }

  .container {
    padding: 0 30px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .hero-section {
    padding: 100px 0 60px;
    margin-top: 65px;
  }

  .content-section {
    padding: 70px 0;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  .product-card {
    border-radius: 12px;
  }

  .product-image-wrapper {
    height: 180px;
  }

  .product-name {
    font-size: 15px;
    min-height: 45px;
  }

  .product-info {
    padding: 16px 12px;
  }

  .page-title {
    font-size: 38px;
  }

  .page-subtitle {
    font-size: 17px;
    margin-bottom: 35px;
  }

  .category-tabs {
    gap: 10px;
    margin-bottom: 40px;
  }

  .category-tab {
    padding: 10px 20px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .products-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .product-image-wrapper {
    height: 240px;
  }

  .product-name {
    font-size: 16px;
  }

  .category-tabs {
    gap: 8px;
    margin-bottom: 30px;
  }

  .category-tab {
    padding: 8px 16px;
    font-size: 13px;
  }
}
</style>